import { Grid, makeStyles } from '@material-ui/core'
import { authSelectors } from 'ducks/auth'
import ImageFieldClearable from 'elements/field/ImageFieldClearable'
import ChipsInput from 'elements/input/ChipsInput'
import DependentInput from 'elements/input/DependentInput'
import { List } from 'elements/react-admin/List'
import { StyledDialog, StyledDialogActions, StyledDialogContent, StyledDialogTitle } from 'layout/StyledDialog'
import {
  Button,
  Checkbox,
  Chip,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  FormControlLabel,
  styled,
  Typography,
} from 'opensolar-ui'
import { useRef, useState } from 'react'
import {
  BooleanInput,
  Create,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  FunctionField,
  ImageField,
  ImageInput,
  ReferenceArrayInput,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  useNotify,
  useRefresh,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import restClient from 'restClient'
import appStorage from 'storage/appStorage'
import { ACTIVATION_TYPE_CHOICES, LENDER_STATUS_CHOICES } from './constants'
import LenderMilestones from './LenderMilestones'

const restClientInstance = restClient(window.API_ROOT + '/api')

const useStyles = makeStyles(() => ({
  col: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  btnWarpper: {
    margin: '5px 0px',
  },
  row: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  text: {
    marginBottom: 10,
  },
}))

const StyledTopToolbar = styled(TopToolbar)({
  display: 'flex',
  gap: 10,
})

const CheckboxRow = styled('div')({
  marginTop: 8,
})

const FilePickerRow = styled('div')({
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

export const LenderList = (props) => {
  const classes = useStyles()
  const history = useHistory()

  const goToRelatedResource = (
    financePartnerId: number,
    resource: 'finance_validation_sets' | 'finance_products' | 'finance_ctas'
  ) => {
    history.push(`/${resource}?finance_partner_id=${financePartnerId}`)
  }
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <List hasSearch={true} {...props}>
        <Datagrid>
          <TextField source="display_name" />
          <FunctionField
            source="finance_product"
            label="Countries"
            sortable={false}
            render={(record: any, source, rest) => {
              return (
                <div className={classes.row}>
                  {record.countries_include_names?.map((orgName) => (
                    <Chip variant="outlined" label={orgName} key={orgName} />
                  ))}
                </div>
              )
            }}
          />
          <FunctionField
            source="finance_product"
            label="Status"
            sortable={false}
            render={(record: any, source, rest) => <div>{record.status?.replaceAll('_', ' ')}</div>}
          />
          <FunctionField
            source="finance_integration"
            label="Actions"
            render={(record: any, source, rest) => (
              <EditButton useListStyle={true} record={record} basePath={rest.basePath} color="inherit" />
            )}
            sortable={false}
          />
          <FunctionField
            source="finance_integration"
            label="Related Records"
            render={(record: any, source, rest) => (
              <div className={classes.col}>
                <div className={classes.btnWarpper}>
                  <Button onClick={() => goToRelatedResource(record.id, 'finance_products')} variant="outlined">
                    <span style={{ width: '150px' }}>View products</span>
                  </Button>
                </div>
                <div className={classes.btnWarpper}>
                  <Button onClick={() => goToRelatedResource(record.id, 'finance_validation_sets')} variant="outlined">
                    <span style={{ width: '150px' }}>View validation sets</span>
                  </Button>
                </div>
                <div className={classes.btnWarpper}>
                  <Button onClick={() => goToRelatedResource(record.id, 'finance_ctas')} variant="outlined">
                    <span style={{ width: '150px' }}>View CTAs</span>
                  </Button>
                </div>
              </div>
            )}
            sortable={false}
          />
        </Datagrid>
      </List>
    </ComponentVersionsInherit>
  )
}

const LenderInputFields = (props) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [isCreatingHubSpotProperty, setIsCreatingHubSpotProperty] = useState(false)
  const validateJSON = (value) => {
    if (!value) return undefined
    try {
      JSON.parse(value)
      return undefined
    } catch (ex) {
      return 'Invalid JSON'
    }
  }

  const handleCreateHubSpotProperty = async () => {
    if (!props.record?.id) {
      notify('Finance partner ID is required', 'warning')
      return
    }

    setIsCreatingHubSpotProperty(true)
    try {
      const response = await restClientInstance('CUSTOM_POST', 'custom', {
        url: `finance_partners/${props.record.id}/create_hubspot_property/`,
      })

      if (response?.data?.success) {
        notify(response.data.message || 'HubSpot property created successfully', 'success')
        refresh()
      } else {
        notify(response?.data?.message || 'Failed to create HubSpot property', 'warning')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create HubSpot property'

      notify(errorMessage, 'warning')
    } finally {
      setIsCreatingHubSpotProperty(false)
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <TextInput source="integration_name" validate={[required()]} fullWidth />
      </Grid>
      <Grid item xs={4}>
        <TextInput source="display_name" validate={[required()]} fullWidth />
      </Grid>
      <Grid item xs={4}>
        <BooleanInput source="is_checkout_finance" label="Is Checkout Finance or Marketplace" />
      </Grid>
      <Grid item xs={3}>
        <SelectInput
          source="status"
          choices={LENDER_STATUS_CHOICES}
          defaultValue="pending"
          fullWidth={true}
          optionText="label"
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={3}>
        <SelectInput
          source="activation_type"
          choices={ACTIVATION_TYPE_CHOICES}
          defaultValue="api_credentials"
          fullWidth={true}
          optionText="label"
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={6}>
        {/* @ts-ignore */}
        <ReferenceArrayInput
          label="Whitelisted Countries"
          reference="countries"
          source="countries_include"
          fullWidth
          style={{ width: '100%' }}
        >
          <ChipsInput optionText="name" optionValue="url" source="countries_include" />
        </ReferenceArrayInput>
      </Grid>
      <Grid item xs={6}>
        <ImageFieldClearable
          record={props.record}
          label="logo_large_contents"
          source="logo_large_contents"
          maxWidth={200}
        />

        <ImageInput
          source="logo_large_contents"
          label="Large Logo"
          accept="image/*"
          placeholder={<Typography variant="body2">Click to upload or drag & drop an image file here</Typography>}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
      </Grid>
      <Grid item xs={6}>
        <ImageFieldClearable
          record={props.record}
          label="logo_small_contents"
          source="logo_small_contents"
          maxWidth={50}
        />

        <ImageInput
          source="logo_small_contents"
          label="Small Logo"
          accept="image/*"
          placeholder={<Typography variant="body2">Click to upload or drag & drop an image file here</Typography>}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
      </Grid>
      <Grid item xs={12}>
        <ImageFieldClearable
          record={props.record}
          label="banner_image_contents"
          source="banner_image_contents"
          maxWidth={600}
        />

        <ImageInput
          source="banner_image_contents"
          label="Banner Image"
          accept="image/*"
          placeholder={<Typography variant="body2">Click to upload or drag & drop an image file here</Typography>}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1Medium">HubSpot Integration</Typography>
        <Typography variant="body2" style={{ marginBottom: 10 }}>
          Create a HubSpot property for this finance partner. This will enable syncing the integration status (1 =
          active, 0 = inactive) in every organization to HubSpot.
        </Typography>
        {props.record?.hubspot_field ? (
          <Typography variant="body2">
            <b>HubSpot property:</b> <code>{props.record.hubspot_field}</code>
          </Typography>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateHubSpotProperty}
            disabled={isCreatingHubSpotProperty}
          >
            {isCreatingHubSpotProperty ? 'Creating...' : 'Create HubSpot Property'}
          </Button>
        )}
      </Grid>
      <Grid item xs={12}>
        <TextInput
          source="banner_content"
          fullWidth
          validate={validateJSON}
          multiline={true}
          rows={30}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">Decision Status Mapping JSON</Typography>
        <Typography variant="body2">
          Used to map the status that the lender sends us to the status that we want to save on the Transaction
        </Typography>
        <TextInput
          label="Decision Status Mapping JSON"
          source="decision_status_mapping_json"
          fullWidth
          validate={validateJSON}
          multiline={true}
          rows={30}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <BooleanInput source="enable_webhooks" />
      </Grid>
      <DependentInput dependsOn="enable_webhooks" value={true}>
        <Grid item xs={12}>
          <Typography variant="body2SemiBold">Status Mapping JSON</Typography>
          <Typography variant="body2">
            Used to map the fields that are sent to us via webhook requests to fields that our webhook handler needs (eg
            project id, transaction_status, etc)
          </Typography>
          <TextInput
            label="Webhook Field Mapping JSON"
            source="webhook_field_mapping_json"
            fullWidth
            validate={validateJSON}
            multiline={true}
            rows={30}
            InputProps={{
              classes: { input: 'code-block' },
            }}
          />
        </Grid>
      </DependentInput>
      <Grid item xs={12}>
        <BooleanInput source="paid_for_offline_deals" />
      </Grid>
      <Grid item xs={12}>
        <BooleanInput source="enable_lender_milestones" />
      </Grid>
      <DependentInput dependsOn="enable_lender_milestones" value={true}>
        <Grid item xs={12}>
          <Typography variant="body2SemiBold">
            Enabling Lender Milestones means that when applications are approved with this lender, or this lender's
            payment option is marked as sold, the milestones on the project will automatically be converted to match the
            lender milestones below
          </Typography>
          <LenderMilestones financePartnerId={props.record.id} />
        </Grid>
      </DependentInput>
    </Grid>
  )
}

const LenderActions = ({ basePath, className, data, hasShow, hasList, hasDelete, resource, deleteLabel, ...rest }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [disableEmailNotification, setDisableEmailNotification] = useState(false)
  const [disableInappNotification, setDisableInappNotification] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const orgId = useSelector(authSelectors.getOrgId) || appStorage.getOrgId()
  const notify = useNotify()

  const handleOpenDialog = () => {
    setDisableEmailNotification(false)
    setDisableInappNotification(false)
    setSelectedFile(null)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null

    if (file && !file.name.toLowerCase().endsWith('.csv')) {
      notify('Please select a CSV file.', 'warning')
      setSelectedFile(null)

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      return
    }

    setSelectedFile(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      notify('Please select a CSV file.', 'warning')
      return
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('disable_email_notification', disableEmailNotification ? 'true' : 'false')
      formData.append('disable_inapp_notification', disableInappNotification ? 'true' : 'false')

      const uploadUrl = `${window.API_BASE_URL}orgs/${orgId}/finance_partners/${data.id}/upload_csv_applications/`

      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: 'Bearer ' + appStorage.getToken(),
        },
      })

      const result = await response.json()

      if (response.ok) {
        notify(result.message || 'CSV uploaded successfully', 'success')
        setDialogOpen(false)
      } else {
        notify(result.message || 'Failed to upload CSV file', 'warning')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload CSV file'
      notify(errorMessage, 'warning')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <StyledTopToolbar className={className} {...rest}>
      {data?.enable_csv_application_upload && (
        <>
          <Button variant="outlined" size="large" onClick={handleOpenDialog}>
            Upload CSV Applications
          </Button>
          <StyledDialog open={dialogOpen} onClose={handleCloseDialog}>
            <StyledDialogTitle>Upload CSV Applications</StyledDialogTitle>
            <StyledDialogContent>
              <Typography variant="body2">
                Application statuses will be updated. Use the options below to suppress notifications about status
                updates.
              </Typography>
              <CheckboxRow>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={disableEmailNotification}
                      onChange={(e) => setDisableEmailNotification(e.target.checked)}
                    />
                  }
                  label="Do not send emails"
                />
              </CheckboxRow>
              <CheckboxRow>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={disableInappNotification}
                      onChange={(e) => setDisableInappNotification(e.target.checked)}
                    />
                  }
                  label="Do not send in-app notifications"
                />
              </CheckboxRow>
              <FilePickerRow>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
                  Choose CSV file
                </Button>
                <Typography variant="body3" color="textSecondary">
                  {selectedFile ? selectedFile.name : 'No file chosen'}
                </Typography>
              </FilePickerRow>
            </StyledDialogContent>
            <StyledDialogActions>
              <Button color="info" variant="outlined" onClick={handleCloseDialog} disabled={isUploading}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleUpload}
                disabled={isUploading || !selectedFile}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </StyledDialogActions>
          </StyledDialog>
        </>
      )}
      {data && hasDelete && (
        <DeleteButton
          name="editDeleteButton"
          undoable={false}
          basePath={basePath}
          record={data}
          resource={resource}
          label={deleteLabel}
        />
      )}
    </StyledTopToolbar>
  )
}

export const LenderEdit = (props) => (
  <ComponentVersionsInherit versions={ComponentVersions_3_0}>
    <Edit {...props} actions={<LenderActions {...props} />}>
      <SimpleForm>
        <LenderInputFields {...props} />
      </SimpleForm>
    </Edit>
  </ComponentVersionsInherit>
)

export const LenderCreate = (props) => {
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Create {...props}>
        <SimpleForm initialValues={{ enabled: true }}>
          <LenderInputFields {...props} />
        </SimpleForm>
      </Create>
    </ComponentVersionsInherit>
  )
}
